import EditorActions from 'components/Common/EditorActions'
import ProfileEditError from '../ProfileEditError'
import ProfileEditResult from '../ProfileEditResult'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Container, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import EditorPhoto, { useEditorPhoto } from '../EditorPhoto'
import EditorNavigation from '../EditorNavigation'
import { useFormik } from 'formik'
import Breadcrumbs from 'components/Common/Breadcrumb'
import { get } from 'helpers/api_helper'
import useProfileEditOperation from '../useProfileEditOperation'
import { initialPlayerEditValues, buildPlayerEditPayload, validatePlayerEdit } from './playerEditForm'
import PlayerEditFields from './PlayerEditFields'
import './playerCreation.scss'

function PlayerEditor({ account, settings, userDetails, match, location, reload }) {
  const id = match.params.id
  const command = useProfileEditOperation({ actorId: userDetails.id || userDetails._id, id, kind: 'player' })
  const photo = useEditorPhoto({ current: account.profile.profilePicture, folder: 'accounts/' + account.profile.userId })
  const releasePhoto = photo.release
  useEffect(() => { if (command.status === 'error') releasePhoto() }, [command.status, releasePhoto])
  const permitted = ['admin', 'superAdmin'].includes(userDetails.role) && userDetails.status === 'activated' && account.profile.status !== 'deleted'
  const save = async payload => {
    if (!permitted) return
    try { const filename = await photo.upload(); photo.retain(); await command.save({ ...payload, profile: { ...payload.profile, profilePicture: filename } }) } catch { /* The photo error is shown beside the upload. */ }
  }
  const [confirmation, setConfirmation] = useState(null)
  const form = useFormik({ initialValues: initialPlayerEditValues(account.profile, account.suggestions),
    validate: values => validatePlayerEdit(values, settings, account.profile),
    onSubmit: values => {
      const profile = buildPlayerEditPayload(values)
      const emailChanged = profile.email !== account.profile.email?.toLowerCase()
      const organizerDisabled = account.profile.isOrganizer && !profile.isOrganizer
      const payload = { revision: account.revision, profile,
        confirmations: { ...(emailChanged ? { email: profile.email } : {}), ...(organizerDisabled ? { organizerDisabled: true } : {}) } }
      if (emailChanged || organizerDisabled) setConfirmation({ payload, emailChanged, organizerDisabled })
      else return save(payload)
    },
  })
  useEffect(() => setConfirmation(null), [form.values])
  const [submitted, setSubmitted] = useState(false)
  const [choiceBusy, setChoiceBusy] = useState({})
  const submit = async event => {
    event.preventDefault()
    if (!permitted || command.locked || photo.uploading || Object.values(choiceBusy).some(Boolean)) return
    setSubmitted(true)
    const errors = await form.validateForm()
    if (Object.keys(errors).length) {
      const field = document.getElementById('player-' + Object.keys(errors)[0])
      let section = field?.closest('details')
      while (section) { section.open = true; section = section.parentElement?.closest('details') }
      field?.focus()
      return
    }
    await form.submitForm()
  }
  const back = { pathname: '/accounts/players/' + id, state: location.state }
  if (command.status === 'saved') return <ProfileEditResult command={command} back={back} label="Open user profile" reload={reload} />
  return <>
    <EditorNavigation dirty={form.dirty || photo.dirty} pending={command.locked} />
    <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
      <div><h4>{account.profile.firstName} {account.profile.lastName}</h4><span className="text-muted">#{account.profile.userId} · {account.profile.status}</span></div>
      <Link className="btn btn-light" to={back}>Back to profile</Link>
    </div>
    {!permitted && <p className="alert alert-info" role="status">{account.profile.status === 'deleted' ? 'Deleted accounts cannot be edited.' : 'This player is read-only with the current account state.'}</p>}
    <Modal isOpen={Boolean(confirmation)} toggle={() => setConfirmation(null)}>
      <ModalHeader toggle={() => setConfirmation(null)}>{confirmation?.emailChanged ? 'Change sign-in email?' : 'Disable organizer access?'}</ModalHeader>
      <ModalBody>
        {confirmation?.emailChanged && <><p>{account.profile.email} → {confirmation.payload.profile.email}</p>
          <p>The password and account status are preserved. Unused invitation and reset links become invalid. For a pending account, send a new invitation explicitly from the profile.</p></>}
        {confirmation?.organizerDisabled && <p>SportyaOS access will be revoked. Organizer details, assigned clubs and existing events are preserved. Re-enabling Organizer does not restore SportyaOS access automatically.</p>}
      </ModalBody><ModalFooter><button type="button" className="btn btn-light" onClick={() => setConfirmation(null)}>Keep editing</button>
        <button type="button" className="btn btn-primary" onClick={() => { const { payload } = confirmation; setConfirmation(null); save(payload) }}>Confirm and save</button></ModalFooter>
    </Modal>
    <form aria-label="Edit player" onSubmit={submit} noValidate>
    <ProfileEditError command={command} reload={reload} />
    {command.locked && <div className="alert alert-info" role="status">
      <p>{command.status === 'unknown' ? 'Saving has not been confirmed. Check the same request before continuing.' : 'Waiting for confirmation…'}</p>
      {command.status === 'unknown' && <><button type="button" className="btn btn-light" onClick={command.check}>Check save status</button><button type="button" className="btn btn-light" onClick={command.resume}>Resume save</button></>}
    </div>}
    {photo.uploading && <p role="status">Uploading photo…</p>}
    {submitted && Object.keys(form.errors).length > 0 && <p className="alert alert-danger" role="alert">Complete the highlighted required details before saving.</p>}
    <EditorPhoto photo={photo} locked={!permitted || command.locked} />
    <PlayerEditFields form={form} settings={settings} locked={!permitted || command.locked || photo.uploading} submitted={submitted} setChoiceBusy={setChoiceBusy}
      legacy={{ birthDateOptional: !account.profile.birthDate, sportSuggested: !account.profile.favoriteSport && Boolean(account.suggestions?.favoriteSport) }} />
    <EditorActions dirty={form.dirty || photo.dirty} pending={command.locked || photo.uploading}>
      <Link className="btn btn-light" to={back}>Cancel</Link>{permitted && <button type="submit" className="btn btn-primary" disabled={command.locked || photo.uploading || Object.values(choiceBusy).some(Boolean)}>Save changes</button>}
    </EditorActions>
  </form></>
}
export function EditPlayer({ userDetails, ...props }) {
  const id = props.match.params.id
  const actorId = userDetails.id || userDetails._id
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let alive = true
    setData(null); setError('')
    Promise.all([get('/admin/player-profiles/' + id), get('/settings')]).then(([account, settings]) => {
      if (!account.data?.profile || !Array.isArray(settings.data?.eligibleCountries) || !Array.isArray(settings.data?.activeSports)) throw new Error('Unavailable profile')
      if (alive) setData({ account: account.data, settings: settings.data })
    }).catch(() => { if (alive) setError('Could not load this player and the available options.') })
    return () => { alive = false }
  }, [id, actorId, retry])
  return <div className="page-content"><Container fluid>
    <Breadcrumbs title="Users" path={{ pathname: "/accounts/players", search: props.location?.state?.playerListSearch || "", state: props.location?.state }} breadcrumbItem="Edit user" />
    <div className="player-creation">
      {error ? <div role="alert">{error} <button className="btn btn-primary" onClick={() => setRetry(value => value + 1)}>Retry player</button></div>
        : !data ? <p role="status">Loading player…</p>
          : <PlayerEditor key={actorId + ':' + id + ':' + retry} {...props} {...data} userDetails={userDetails} reload={() => setRetry(value => value + 1)} />}
    </div>
  </Container></div>
}
export default function ConnectedEditPlayer(props) {
  const userDetails = useSelector(state => state.session.userDetails) || {}
  return <EditPlayer {...props} userDetails={userDetails} />
}
